/**
 *
 * API for load tags
 */

import StringUtils from '../../../utils/stringUtils';
import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';
import { persistentTripsAdapter, persistentTripsSelector, tripApiUtil } from '../trips/tripApi';
import { SearchTripsParams, TRIP_STATUS } from '../trips/trips.model';
import {
  ICreateLoadTag,
  IEditLoadTag,
  IRemoveTagLoad,
  ISearchTagsResponse
} from './loadTags.model';

const loadTagsApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    searchTags: builder.query<
      { content: ISearchTagsResponse[]; total: number },
      { params?: BaseQueryParams; filterParams?: { search: string } }
    >({
      query: (query) => {
        const { params, filterParams } = query;

        return {
          url: `v3/tags/search`,
          method: 'POST',
          params: { ...params },
          body: { ...filterParams }
        };
      },
      transformResponse: (response: { data: ISearchTagsResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'TagsList' as const, id })),
              { type: 'TagsList', id: 'TAGS_LIST' }
            ]
          : [{ type: 'TagsList', id: 'TAGS_LIST' }]
    }),
    tagById: builder.query<ISearchTagsResponse, string>({
      query: (id) => `v3/tags/${id}`,
      transformResponse: (response: { data: ISearchTagsResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'TagsList' as const, id }]
    }),
    createTag: builder.mutation<
      ISearchTagsResponse,
      ICreateLoadTag & { searchTripParams: SearchTripsParams } & { tagsAreSeparated: boolean }
    >({
      query: (body) => {
        const { loadId, ...rest } = body;
        return {
          url: `orders/${loadId}/tags`,
          method: 'POST',
          body: { ...rest }
        };
      },
      transformResponse: (response: { data: any; message: string }) => response.data,
      onQueryStarted: async (
        { loadId, tagId, name, searchTripParams, tagsAreSeparated },
        { dispatch, queryFulfilled }
      ) => {
        // If tags are separated, we don't need to update the trips inside the order
        if (tagsAreSeparated) return;
        const { data: newTag } = await queryFulfilled;

        StringUtils.getEnumValues(TRIP_STATUS).forEach((status) => {
          const upsert = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: { ...searchTripParams.filters, status: [status] as TRIP_STATUS[] }
              },
              (tripsData) => {
                const tripsFromSameOrder = persistentTripsSelector
                  .selectAll(tripsData.content)
                  .filter((trip) => trip.order_id === loadId);

                // Add the new tag to the trips
                tripsFromSameOrder.forEach((trip) => {
                  const updatedTags = [newTag, ...trip.tags.filter((t) => t.id !== newTag.id)];
                  const updatedTrip = {
                    ...trip,
                    tags: updatedTags
                  };

                  // update the cache
                  persistentTripsAdapter.upsertOne(tripsData.content, updatedTrip);
                });
              }
            )
          );
          queryFulfilled.catch(() => {
            upsert.undo();
          });
        });
      },
      invalidatesTags: (result, error, { loadId }) => [
        { type: 'TagsList', id: 'TAGS_LIST' },
        {
          type: 'Orders' as const,
          loadId
        },
        { type: 'Trips', id: 'TRIPS_LIST' }
      ]
    }),
    editTag: builder.mutation<
      ISearchTagsResponse,
      IEditLoadTag & { orderId: string } & { searchTripParams: SearchTripsParams }
    >({
      query: (body) => {
        const { tagId, ...rest } = body;

        return {
          url: `v3/tags/${tagId}`,
          method: 'PATCH',
          body: { ...rest }
        };
      },
      transformResponse: (response: { data: any; message: string }) => response.data,
      onQueryStarted: async (
        { tagId, name, color, searchTripParams },
        { dispatch, queryFulfilled }
      ) => {
        // We have to update all the trips that have this tag on every status
        StringUtils.getEnumValues(TRIP_STATUS).forEach((status) => {
          const upsert = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: { ...searchTripParams.filters, status: [status] as TRIP_STATUS[] }
              },
              (tripsData) => {
                persistentTripsSelector.selectAll(tripsData.content).forEach((trip) => {
                  // check loads in cache if they have the tag we are editing
                  const index = trip.tags.findIndex((t) => t.id === tagId);
                  if (index !== -1) {
                    const updatedTags = trip.tags.slice(0);

                    // update the tag
                    updatedTags[index] = {
                      ...trip.tags[index],
                      name: name || trip.tags[index].name,
                      color: color || trip.tags[index].color
                    };
                    const updatedTrip = {
                      ...trip,
                      tags: updatedTags
                    };

                    // update the cache
                    persistentTripsAdapter.upsertOne(tripsData.content, updatedTrip);
                  }
                });
              }
            )
          );
          queryFulfilled.catch(() => {
            upsert.undo();
          });
        });
      },
      invalidatesTags: (result, error, { tagId, orderId }) => [
        { type: 'TagsList', id: tagId },
        { type: 'Orders' },
        { type: 'Orders' as const, orderId },
        { type: 'Trips' },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' }
      ]
    }),
    removeTag: builder.mutation<any, IRemoveTagLoad & { searchTripParams: SearchTripsParams }>({
      query: (body) => {
        const { loadId, tagId } = body;
        return {
          url: `orders/${loadId}/tags/${tagId}`,
          method: 'DELETE'
        };
      },
      transformResponse: (response: { data: any; message: string }) => response.data,
      onQueryStarted: async ({ loadId, tagId, searchTripParams }, { dispatch, queryFulfilled }) => {
        StringUtils.getEnumValues(TRIP_STATUS).forEach((status) => {
          const upsert = dispatch(
            tripApiUtil.updateQueryData(
              'getActivePersistentTrips',
              {
                ...searchTripParams,
                filters: { ...searchTripParams.filters, status: [status] as TRIP_STATUS[] }
              },
              (tripsData) => {
                const tripsFromSameOrder = persistentTripsSelector
                  .selectAll(tripsData.content)
                  .filter((trip) => trip.order_id === loadId);

                // Add the new tag to the trips
                tripsFromSameOrder.forEach((trip) => {
                  // Remove the tag from the trip
                  const updatedTags = trip.tags.filter((t) => t.id !== tagId) || [];
                  const updatedTrip = {
                    ...trip,
                    tags: updatedTags
                  };

                  // update the cache
                  persistentTripsAdapter.upsertOne(tripsData.content, updatedTrip);
                });
              }
            )
          );
          queryFulfilled.catch(() => {
            upsert.undo();
          });
        });
      },
      invalidatesTags: (result, error, { loadId }) => [
        {
          type: 'Orders' as const,
          loadId
        },
        { type: 'ActiveTrips', id: 'TRIPS_LIST' },
        { type: 'Trips', id: 'TRIPS_LIST' }
      ]
    }),
    deleteTag: builder.mutation({
      query: (id: string) => ({
        url: `v3/tags/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          loadTagsApi.util.updateQueryData('searchTags', {}, (tags) => {
            const index = tags.content.findIndex((c) => c.id === id);
            delete tags.content[index];
            tags.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'TagsList', id },
        { type: 'TagsList', id: 'TAGS_LIST' }
      ]
    })
  })
});

export const {
  useSearchTagsQuery,
  useTagByIdQuery,
  useCreateTagMutation,
  useEditTagMutation,
  useRemoveTagMutation,
  useDeleteTagMutation
} = loadTagsApi;
